import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { 
  Menu, ShoppingCart, User, LogOut, Globe, 
  Home, Utensils, Wind, LayoutDashboard, Truck, Store
} from 'lucide-react';

export const Navbar = () => {
  const { user, logout, isAdmin, isDriver, isRestaurantManager } = useAuth();
  const { itemCount, setIsOpen } = useCart();
  const { t, toggleLanguage, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const navLinks = [
    { path: '/', label: t('home'), icon: Home },
    { path: '/restaurants', label: t('restaurants'), icon: Utensils },
    { path: '/shisha', label: t('shisha'), icon: Wind },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 glass border-b border-white/20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#FF6B35] flex items-center justify-center">
              <span className="text-white font-bold text-lg">W</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-xl text-[#004E64]">WEGOO</span>
              <span className="text-xs text-[#9FA2B4] block -mt-1">Troia Delivery</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
                  isActive(link.path) 
                    ? 'bg-[#FF6B35] text-white' 
                    : 'text-[#004E64] hover:bg-[#004E64]/5'
                }`}
              >
                <link.icon size={18} />
                <span className="font-medium">{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="rounded-full"
              data-testid="language-toggle"
            >
              <Globe size={20} />
              <span className="ml-1 text-xs font-bold">{language.toUpperCase()}</span>
            </Button>

            {/* Cart Button - Only show when logged in */}
            {user && (
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full relative"
                onClick={() => setIsOpen(true)}
                data-testid="cart-button"
              >
                <ShoppingCart size={20} />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF6B35] text-white text-xs rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Button>
            )}

            {/* User Menu */}
            {user ? (
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="admin-link">
                      <LayoutDashboard size={20} />
                    </Button>
                  </Link>
                )}
                {isDriver && !isAdmin && (
                  <Link to="/driver">
                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="driver-link">
                      <Truck size={20} />
                    </Button>
                  </Link>
                )}
                {isRestaurantManager && !isAdmin && (
                  <>
                    <Link to="/pos">
                      <Button variant="ghost" size="icon" className="rounded-full bg-[#FF6B35]/10 hover:bg-[#FF6B35]/20" data-testid="restaurant-pos-link" title="POS — Receber Pedidos">
                        <Store size={20} className="text-[#FF6B35]" />
                      </Button>
                    </Link>
                    <Link to="/restaurant-portal">
                      <Button variant="ghost" size="icon" className="rounded-full" data-testid="restaurant-portal-link" title="Portal Financeiro">
                        <LayoutDashboard size={20} className="text-[#2EC4B6]" />
                      </Button>
                    </Link>
                  </>
                )}
                <Link to="/profile">
                  <Button variant="ghost" size="icon" className="rounded-full" data-testid="profile-link">
                    <User size={20} />
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  <LogOut size={20} />
                </Button>
              </div>
            ) : (
              <Link to="/login">
                <Button className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full" data-testid="login-button">
                  {t('login')}
                </Button>
              </Link>
            )}

            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="mobile-menu">
                  <Menu size={24} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <div className="flex flex-col gap-4 mt-8">
                  {navLinks.map(link => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive(link.path) 
                          ? 'bg-[#FF6B35] text-white' 
                          : 'text-[#004E64] hover:bg-[#004E64]/5'
                      }`}
                    >
                      <link.icon size={20} />
                      <span className="font-medium">{link.label}</span>
                    </Link>
                  ))}
                  
                  {user && (
                    <>
                      <hr className="border-gray-200" />
                      <Link
                        to="/orders"
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#004E64] hover:bg-[#004E64]/5"
                      >
                        <ShoppingCart size={20} />
                        <span className="font-medium">{t('orders')}</span>
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#004E64] hover:bg-[#004E64]/5"
                        >
                          <LayoutDashboard size={20} />
                          <span className="font-medium">{t('admin')}</span>
                        </Link>
                      )}
                      {isDriver && (
                        <Link
                          to="/driver"
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#004E64] hover:bg-[#004E64]/5"
                        >
                          <Truck size={20} />
                          <span className="font-medium">{t('driver')}</span>
                        </Link>
                      )}
                      {isRestaurantManager && (
                        <>
                          <Link
                            to="/pos"
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#004E64] hover:bg-[#FF6B35]/10"
                            data-testid="mobile-restaurant-pos-link"
                          >
                            <Store size={20} className="text-[#FF6B35]" />
                            <span className="font-medium">POS — {language === 'pt' ? 'Pedidos' : 'Orders'}</span>
                          </Link>
                          <Link
                            to="/restaurant-portal"
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#004E64] hover:bg-[#004E64]/5"
                            data-testid="mobile-restaurant-portal-link"
                          >
                            <LayoutDashboard size={20} className="text-[#2EC4B6]" />
                            <span className="font-medium">{language === 'pt' ? 'Portal Financeiro' : 'Financial Portal'}</span>
                          </Link>
                        </>
                      )}
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
